import React, { useEffect, useState } from 'react';
import { Modal, Form, DatePicker, InputNumber, Row, Col, message } from 'antd';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const BasicSalaryModal = ({ visible, onCancel, onSuccess, employeeId, editingRecord, dateOfJoining }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Disable dates before joining date
    const disabledDate = (current) => {
        if (!dateOfJoining) return false;
        return current && current < dayjs(dateOfJoining).startOf('day');
    };

    useEffect(() => {
        if (visible) {
            if (editingRecord) {
                form.setFieldsValue({
                    ...editingRecord,
                    effective_date: editingRecord.effective_date ? dayjs(editingRecord.effective_date) : null
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, editingRecord, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = {
                employee: employeeId,
                effective_date: values.effective_date.format('YYYY-MM-DD'),
                basic_salary: values.basic_salary,
                hourly_rate: values.hourly_rate || 0,
                ot_rate: values.ot_rate || 0,
                hours_for_hourly_rate: values.hours_for_hourly_rate || 0
            };

            if (editingRecord) {
                await adminactions.updateBasicSalary(editingRecord.id, data);
                message.success('Basic salary updated successfully');
            } else {
                await adminactions.createBasicSalary(data);
                message.success('Basic salary created successfully');
            }

            onSuccess();
            form.resetFields();
        } catch (error) {
            message.error('Failed to save basic salary');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={editingRecord ? 'Edit Basic Salary' : 'Add Basic Salary'}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={700}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    hourly_rate: 0,
                    ot_rate: 0,
                    hours_for_hourly_rate: 0
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="effective_date"
                            label="Effective Date"
                            rules={[{ required: true, message: 'Please select effective date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="basic_salary"
                            label="Basic Salary"
                            rules={[{ required: true, message: 'Please enter basic salary' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={100}
                                prefix="₹"
                                placeholder="Enter basic salary"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="hourly_rate"
                            label="Hourly Rate"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                placeholder="Enter hourly rate"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ot_rate"
                            label="Overtime Rate"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                prefix="₹"
                                placeholder="Enter OT rate"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="hours_for_hourly_rate"
                            label="Standard Hours"
                            tooltip="Standard hours for hourly rate calculation"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.5}
                                placeholder="Enter standard hours"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default BasicSalaryModal;
